import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom/cjs/react-router-dom.min';
import { toast } from 'react-toastify';
import Layout from '../../components/common/pageLayout';
import './finalDrawing.css';
import { v2_document_type } from '../../actions/v2/masterData';
import { v3_add_document_type } from '../../actions/v3/masterData';
import { v2_ships } from '../../actions/v2/projectAndVessel';
import {
    v3_dms_create_files,
    v3_dms_create_folder,
    v3_dms_delete_file,
    v3_dms_delete_folder,
    v3_dms_file_download_url,
    v3_dms_file_preview_url,
    v3_dms_files,
    v3_dms_folders,
    v3_dms_update_file,
    v3_dms_update_folder,
} from '../../actions/v3/dms';
import FolderTree, { folderPath } from '../../components/FinalDrawing/DMS/FolderTree';
import { FolderModal, MoveModal, ShareModal, SharedLinksPanel, UploadModal } from '../../components/FinalDrawing/DMS/Modals';

const fail = (res) => toast.error(res.error?.message || res.error || 'Something went wrong.');

const formatDate = (value) => (value ? new Date(value).toLocaleDateString() : '—');

const Index = () => {
    const location = useLocation();
    const { projectId, projectName } = location.state || {};

    const [view, setView] = useState('vessels');            // 'folders' | 'vessels'
    const [folders, setFolders] = useState([]);
    const [rootFileCount, setRootFileCount] = useState(0);
    const [folderId, setFolderId] = useState('root');
    const [ships, setShips] = useState([]);
    const [shipId, setShipId] = useState('project');        // '' = every vessel, 'project' = untagged
    const [documentTypes, setDocumentTypes] = useState([]);
    const [documentTypeId, setDocumentTypeId] = useState(''); // '' = all, 'none' = untagged
    const [files, setFiles] = useState([]);
    const [keyword, setKeyword] = useState('');
    const [search, setSearch] = useState('');
    const [openMenu, setOpenMenu] = useState(null);
    const [dialog, setDialog] = useState(null);             // {type, ...payload}

    const currentFolder = useMemo(
        () => folders.find((folder) => String(folder.ID) === String(folderId)) || null,
        [folders, folderId]
    );
    const breadcrumb = useMemo(
        () => (folderId === 'root' ? [] : folderPath(folders, folderId)),
        [folders, folderId]
    );
    const childFolders = useMemo(() => {
        if (search) return [];
        return folders.filter((folder) => (
            folderId === 'root' ? !folder.PARENT_ID : String(folder.PARENT_ID) === String(folderId)
        ));
    }, [folders, folderId, search]);

    const loadFolders = useCallback(() => {
        if (!projectId) return;
        v3_dms_folders(projectId).then((res) => {
            if (!res.status) return fail(res);
            setFolders(res.result.folders);
            setRootFileCount(res.result.rootFileCount);
        });
    }, [projectId]);

    const loadFiles = useCallback(() => {
        if (!projectId) return;
        v3_dms_files({
            projectId,
            folderId: search ? undefined : folderId,
            shipId: shipId || undefined,
            documentTypeId: documentTypeId || undefined,
            keyword: search || undefined,
        }).then((res) => {
            if (!res.status) return fail(res);
            setFiles(res.result.files);
        });
    }, [projectId, folderId, shipId, documentTypeId, search]);

    useEffect(loadFolders, [loadFolders]);
    useEffect(loadFiles, [loadFiles]);

    useEffect(() => {
        if (!projectId) return;
        v2_ships(null, projectId, null).then((res) => {
            if (res.status) setShips(res.result.ships || res.result || []);
        });
    }, [projectId]);

    const loadDocumentTypes = useCallback(() => v2_document_type().then((res) => {
        const list = res.status ? (res.result || []) : [];
        setDocumentTypes(list);
        return list;
    }), []);

    useEffect(() => { loadDocumentTypes(); }, [loadDocumentTypes]);

    // The create endpoint only returns a message, so the new row is looked up by
    // name on reload to hand its ID back for auto-selection.
    const createDocumentType = (name) => v3_add_document_type({ documentTypeName: name }).then((res) => {
        if (!res.status) { fail(res); return null; }
        toast.success(res.result?.message || 'Document type added.');
        return loadDocumentTypes().then((list) => {
            const created = list.find((type) => type.DOCUMENT_TYPE === name);
            return created ? String(created.ID) : null;
        });
    });

    // Debounced so a keystroke does not fire a project-wide search each time.
    useEffect(() => {
        const timer = setTimeout(() => setSearch(keyword.trim()), 350);
        return () => clearTimeout(timer);
    }, [keyword]);

    const refresh = () => { loadFolders(); loadFiles(); };
    const closeDialog = () => setDialog(null);

    const run = (promise, message) => promise.then((res) => {
        if (!res.status) return fail(res);
        toast.success(message || res.result?.message);
        closeDialog();
        refresh();
    });

    const deleteFolderAction = (folder) => {
        if (!window.confirm(`Delete "${folder.FOLDER_NAME}" and everything inside it?`)) return;
        run(v3_dms_delete_folder(folder.ID)).then(() => {
            if (String(folderId) === String(folder.ID)) setFolderId('root');
        });
    };

    const deleteFileAction = (file) => {
        if (!window.confirm(`Delete "${file.DISPLAY_NAME}"?`)) return;
        run(v3_dms_delete_file(file.ID));
    };

    if (!projectId) {
        return (
            <Layout title="Final Drawing">
                <p className="text-muted">No project selected. Please pick a project first.</p>
            </Layout>
        );
    }

    return (
        <Layout title={`Documents | ${projectName}`}>
            <div className="row">
                <div className="col-lg-12">
                    <div className="card">
                        <div className="card-body">

                            <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
                                <div className="btn-group btn-group-sm dms-view-toggle" role="group">
                                    <button
                                        type="button"
                                        className={`btn ${view === 'folders' ? 'active' : ''}`}
                                        onClick={() => { setView('folders'); setShipId(''); }}
                                    >
                                        <i className="mdi mdi-folder-outline me-1" />Folders
                                    </button>
                                    <button
                                        type="button"
                                        className={`btn ${view === 'vessels' ? 'active' : ''}`}
                                        onClick={() => { setView('vessels'); setShipId('project'); }}
                                    >
                                        <i className="mdi mdi-ferry me-1" />Vessels
                                    </button>
                                </div>

                                <div className="d-flex flex-wrap align-items-center gap-2 dms-toolbar-actions">
                                    <input
                                        className="form-control form-control-sm dms-search"
                                        placeholder="Search documents…"
                                        value={keyword}
                                        onChange={(event) => setKeyword(event.target.value)}
                                    />
                                    <select
                                        className="form-select form-select-sm w-auto"
                                        value={documentTypeId}
                                        onChange={(event) => setDocumentTypeId(event.target.value)}
                                    >
                                        <option value="">All document types</option>
                                        <option value="none">Untagged</option>
                                        {documentTypes.map((type) => (
                                            <option key={type.ID} value={type.ID}>{type.DOCUMENT_TYPE}</option>
                                        ))}
                                    </select>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-primary"
                                        onClick={() => setDialog({ type: 'folder', mode: 'create' })}
                                    >
                                        <i className="fa fa-plus-circle" /> New folder
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-primary"
                                        onClick={() => setDialog({ type: 'upload' })}
                                    >
                                        <i className="mdi mdi-upload" /> Upload
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-secondary"
                                        onClick={() => setDialog({ type: 'shares' })}
                                    >
                                        <i className="mdi mdi-link-variant" /> Shared links
                                    </button>
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-md-3 border-end dms-browser-rail">
                                    {view === 'vessels' && (
                                        <div className="mb-3">
                                            <p className="text-muted small text-uppercase mb-1">Vessels</p>
                                            <div
                                                className={`dms-rail-item ${shipId === 'project' ? 'selected' : ''}`}
                                                onClick={() => setShipId('project')}
                                            >
                                                Project-wide
                                            </div>
                                            {ships.map((ship) => (
                                                <div
                                                    key={ship.ID}
                                                    className={`dms-rail-item text-truncate ${String(shipId) === String(ship.ID) ? 'selected' : ''}`}
                                                    onClick={() => setShipId(String(ship.ID))}
                                                    title={`${ship.HULL_NUMBER} — ${ship.SHIP_NAME}`}
                                                >
                                                    <i className="mdi mdi-ferry me-2" />{ship.HULL_NUMBER} — {ship.SHIP_NAME}
                                                </div>
                                            ))}
                                            <hr />
                                        </div>
                                    )}

                                    <p className="text-muted small text-uppercase mb-1">Folders</p>
                                    <FolderTree
                                        folders={folders}
                                        rootFileCount={rootFileCount}
                                        selectedId={folderId}
                                        onSelect={(id) => { setFolderId(id); setKeyword(''); }}
                                    />
                                </div>

                                <div className="col-md-9">
                                    {search ? (
                                        <p className="mb-2">
                                            <i className="mdi mdi-magnify" /> {files.length} result(s) for “{search}”
                                            <button type="button" className="btn btn-sm btn-link" onClick={() => setKeyword('')}>Clear</button>
                                        </p>
                                    ) : (
                                        <nav className="mb-2">
                                            <span role="button" className="text-primary" onClick={() => setFolderId('root')}>Home</span>
                                            {breadcrumb.map((folder) => (
                                                <span key={folder.ID}>
                                                    <span className="mx-1 text-muted">/</span>
                                                    <span role="button" className="text-primary" onClick={() => setFolderId(folder.ID)}>
                                                        {folder.FOLDER_NAME}
                                                    </span>
                                                </span>
                                            ))}
                                        </nav>
                                    )}

                                    <div className="appraisal-report-table-responsive">
                                        <table className="plan-approval-table">
                                            <thead>
                                                <tr>
                                                    <th>Name</th>
                                                    <th className="w200">{search ? 'Location' : 'Vessel'}</th>
                                                    <th className="w150">Document type</th>
                                                    <th className="w150">Uploaded by</th>
                                                    <th className="w120">Modified</th>
                                                    <th className="w120">Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {childFolders.map((folder) => (
                                                    <tr key={`folder-${folder.ID}`}>
                                                        <td>
                                                            <span role="button" onClick={() => setFolderId(folder.ID)}>
                                                                <i className="mdi mdi-folder text-warning me-2" />
                                                                {folder.FOLDER_NAME}
                                                            </span>
                                                        </td>
                                                        <td className="text-muted">{folder.FILE_COUNT} file(s)</td>
                                                        <td>—</td>
                                                        <td>—</td>
                                                        <td>{formatDate(folder.CREATED_ON)}</td>
                                                        <td>
                                                            <RowMenu
                                                                id={`folder-${folder.ID}`}
                                                                openMenu={openMenu}
                                                                setOpenMenu={setOpenMenu}
                                                                items={[
                                                                    { label: 'Share', icon: 'mdi-link-variant', onClick: () => setDialog({ type: 'share', target: { type: 'folder', id: folder.ID, name: folder.FOLDER_NAME } }) },
                                                                    { label: 'Rename', icon: 'mdi-pencil', onClick: () => setDialog({ type: 'folder', mode: 'rename', folder }) },
                                                                    { label: 'Move', icon: 'mdi-folder-move', onClick: () => setDialog({ type: 'move', folder }) },
                                                                    { label: 'Delete', icon: 'mdi-delete', danger: true, onClick: () => deleteFolderAction(folder) },
                                                                ]}
                                                            />
                                                        </td>
                                                    </tr>
                                                ))}

                                                {files.map((file) => (
                                                    <tr key={`file-${file.ID}`}>
                                                        <td>
                                                            <a href={v3_dms_file_preview_url(file.ID)} target="_blank" rel="noopener noreferrer">
                                                                <i className="mdi mdi-file-document-outline me-2" />
                                                                {file.DISPLAY_NAME}
                                                            </a>
                                                        </td>
                                                        <td className="text-muted">
                                                            {search
                                                                ? file.FOLDER_PATH
                                                                : (file.SHIP_ID ? `${file.HULL_NUMBER} — ${file.SHIP_NAME}` : 'Project-wide')}
                                                        </td>
                                                        <td className="text-muted">{file.DOCUMENT_TYPE || '—'}</td>
                                                        <td>{file.CREATED_BY_NAME || '—'}</td>
                                                        <td>{formatDate(file.MODIFIED_ON || file.CREATED_ON)}</td>
                                                        <td>
                                                            <RowMenu
                                                                id={`file-${file.ID}`}
                                                                openMenu={openMenu}
                                                                setOpenMenu={setOpenMenu}
                                                                items={[
                                                                    { label: 'Preview', icon: 'mdi-eye', href: v3_dms_file_preview_url(file.ID) },
                                                                    { label: 'Download', icon: 'mdi-download', href: v3_dms_file_download_url(file.ID) },
                                                                    { label: 'Share', icon: 'mdi-link-variant', onClick: () => setDialog({ type: 'share', target: { type: 'file', id: file.ID, name: file.DISPLAY_NAME } }) },
                                                                    { label: 'Rename', icon: 'mdi-pencil', onClick: () => setDialog({ type: 'renameFile', file }) },
                                                                    { label: 'Move', icon: 'mdi-folder-move', onClick: () => setDialog({ type: 'move', file }) },
                                                                    { label: 'Delete', icon: 'mdi-delete', danger: true, onClick: () => deleteFileAction(file) },
                                                                ]}
                                                            />
                                                        </td>
                                                    </tr>
                                                ))}

                                                {childFolders.length === 0 && files.length === 0 && (
                                                    <tr>
                                                        <td colSpan={6} className="text-muted text-center py-4">
                                                            {search ? 'No documents match your search.' : 'This folder is empty.'}
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            </div>

            {dialog?.type === 'folder' && (
                <FolderModal
                    mode={dialog.mode}
                    initialName={dialog.folder?.FOLDER_NAME || ''}
                    onClose={closeDialog}
                    onSave={(name) => run(
                        dialog.mode === 'rename'
                            ? v3_dms_update_folder({ id: dialog.folder.ID, folderName: name })
                            : v3_dms_create_folder({
                                projectId,
                                parentId: folderId === 'root' ? null : folderId,
                                folderName: name,
                            })
                    )}
                />
            )}

            {dialog?.type === 'renameFile' && (
                <FolderModal
                    mode="rename"
                    initialName={dialog.file.DISPLAY_NAME}
                    onClose={closeDialog}
                    onSave={(name) => run(v3_dms_update_file({ id: dialog.file.ID, displayName: name }))}
                />
            )}

            {dialog?.type === 'move' && (
                <MoveModal
                    folders={folders}
                    movingFolderId={dialog.folder?.ID || null}
                    currentFolderId={dialog.folder ? dialog.folder.PARENT_ID : dialog.file.FOLDER_ID}
                    onClose={closeDialog}
                    onMove={(target) => run(
                        dialog.folder
                            ? v3_dms_update_folder({ id: dialog.folder.ID, parentId: target })
                            : v3_dms_update_file({ id: dialog.file.ID, folderId: target })
                    )}
                />
            )}

            {dialog?.type === 'upload' && (
                <UploadModal
                    folderName={currentFolder?.FOLDER_NAME || 'Home'}
                    ships={ships}
                    documentTypes={documentTypes}
                    onCreateDocumentType={createDocumentType}
                    onClose={closeDialog}
                    onUpload={(uploaded, ship, documentType) => run(v3_dms_create_files({
                        projectId,
                        folderId: folderId === 'root' ? null : folderId,
                        shipId: ship,
                        documentTypeId: documentType,
                        files: uploaded,
                    }))}
                />
            )}

            {dialog?.type === 'share' && (
                <ShareModal projectId={projectId} target={dialog.target} onClose={closeDialog} />
            )}

            {dialog?.type === 'shares' && (
                <SharedLinksPanel projectId={projectId} onClose={closeDialog} />
            )}
        </Layout>
    );
};

const RowMenu = ({ id, openMenu, setOpenMenu, items }) => (
    <div className="dropdown">
        <button
            type="button"
            className="btn btn-sm btn-link text-dark"
            onClick={() => setOpenMenu(openMenu === id ? null : id)}
        >
            <i className="mdi mdi-dots-vertical" />
        </button>
        {openMenu === id && (
            <>
                {/* Click-away layer: keeps the menu closing logic in one place. */}
                <div
                    style={{ position: 'fixed', inset: 0, zIndex: 1 }}
                    onClick={() => setOpenMenu(null)}
                />
                <ul className="dropdown-menu show" style={{ position: 'absolute', right: 0, zIndex: 2 }}>
                    {items.map((item) => (
                        <li key={item.label}>
                            {item.href ? (
                                <a
                                    className="dropdown-item"
                                    href={item.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={() => setOpenMenu(null)}
                                >
                                    <i className={`mdi ${item.icon} me-2`} />{item.label}
                                </a>
                            ) : (
                                <button
                                    type="button"
                                    className={`dropdown-item ${item.danger ? 'text-danger' : ''}`}
                                    onClick={() => { setOpenMenu(null); item.onClick(); }}
                                >
                                    <i className={`mdi ${item.icon} me-2`} />{item.label}
                                </button>
                            )}
                        </li>
                    ))}
                </ul>
            </>
        )}
    </div>
);

export default Index;
